import { ReactElement, useRef, useState } from 'react';
import logo from '../../assets/images/onboardPage/hypercept_logo.png'
import { theme } from '../../utils/theme/godzilla_light';
import { Box, Button, Checkbox, Link, Typography } from '@mui/material';
import { Trans } from 'react-i18next';
import { dataPrivacyLink } from '../../assets/data';

const styles = {
    cardWrapper: {
        p: "3.125rem",
        boxShadow: '0px 1px 5px 0px rgba(37, 37, 37, 0.12), 0px 2px 2px 0px rgba(37, 37, 37, 0.14), 0px 3px 1px -2px rgba(37, 37, 37, 0.20)',
        backgroundColor: "#fff",
        borderRadius: "8px",
        maxWidth: "720px",
        maxHeight: "957px",
        mt: "2.375rem",
    },
    checkBoxWrapper: {
        display: "flex",
        alignItems: "center",
        flexDirection: "row",
        justifyContent: "space-between",
        flex: 1,
        mt: "2.875rem",
    },
    pageTitleStyle: {
        ...theme.typography.opticianBodyRegular,
        fontSize: "2rem",
        mt: "1.875rem"
    },
    descriptionText: {
        ...theme.typography.poppinsBodyRegular,
        fontSize: '16px',
         lineHeight: '21.79px',
        mt: "1.875rem",
        textAlign: "left"
    },
    scrollContentWrapper: {
        maxHeight: "350px",
        overflow: "auto",
        mt: "1.875rem"
    },
    termsContentTitle: {
        ...theme.typography.poppinsBodySemiBold,
         fontSize: '16px',
        textAlign: "left",
        color:"#1E1E23"
    },
    termsContent: {
        ...theme.typography.body1Regular,
        marginTop: "1.5rem",
        textAlign: "left"
    },
    buttonLabel: {
        ...theme.typography.body1SemiBold,
        color: "#fff"
    },
    pageLinkText: {
        ...theme.typography.body1Regular,
        color: theme?.palette?.primary?.main,
        textDecoration: "underline",
        textDecorationColor: theme?.palette?.primary?.main,
        fontWeight: 700,
        fontSize: "16px"
    },
};

interface ICommonTermsCardProps {
    pageTitle: string;
    checkboxContent?: string;
    pageDescription?: string;
    onButtonClick?: (checked: boolean) => void;
    data?: any[];
    buttonLabel?: string;
    mode?: "privacy" | "terms"
}

export const CommonTermsCard = (props: ICommonTermsCardProps) => {
    const { pageTitle, checkboxContent, pageDescription, onButtonClick, data, buttonLabel, mode } = props
    const [checked, setChecked] = useState(false)
    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
    const scrollRef = useRef(null);
    const isPrivacy = mode === "privacy"

    const handleScroll = () => {
        if (scrollRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
            if (scrollTop + clientHeight >= scrollHeight - 5) {
                setIsScrolledToBottom(true);
            }
        }
    };

    const i18nCustomComponents = {
        br: <br />,
        b: <b></b>,
        nbsp:<>{'\u00A0'}</>,
        // eslint-disable-next-line
        a:<a href="mailto:support@hypercept-ga.com"></a>
    } as unknown as { [key: string]: ReactElement };
    return (
        <Box sx={ isPrivacy ? {...styles.cardWrapper, height:"460px"} : styles.cardWrapper}>
            <img src={logo} style={{ height: "51px", width: "400px" }} alt='astellas-onboard-logo' />
            <Typography sx={styles.pageTitleStyle}>{pageTitle}</Typography>
            <Box
                ref={scrollRef}
                onScroll={handleScroll}
                className={'customScrollContainer'}
                sx={styles.scrollContentWrapper}>
                <Typography sx={styles.descriptionText}>
                    <Trans
                        i18nKey={pageDescription}
                        components={{ 
                            br:<br />,
                            b: <b></b>,
                            Link: <Link href={dataPrivacyLink} sx={[styles.pageLinkText]} target="_blank" rel="noopener noreferrer"></Link>
                        }} />
                </Typography>
                <Box>
                    {isPrivacy ?
                        data?.length && data.map((item: any, index: number) => {
                            return (
                                <Box sx={{ mt: index !== 0 ? "1.25rem" : "0px" }} key={index + 1}>
                                    <Typography sx={styles.termsContent}>
                                        <Trans
                                            i18nKey={item.content}
                                            components={{ 
                                                br:<br />,
                                                b: <b></b>
                                        }} />
                                    </Typography>
                                </Box>
                            )
                        })
                        :
                        data?.length && data.map((item: any, index: number) => {
                            return (
                                <Box sx={{ mt: index !== 0 ? "1.25rem" : "1.26rem" }} key={index + 1}>
                                    <Typography sx={styles.termsContentTitle}>{index + 1}. {item.title}</Typography>
                                    <Typography sx={styles.termsContent}>
                                        <Trans
                                            i18nKey={item?.content}
                                            components={i18nCustomComponents} />
                                    </Typography>
                                    <ol type="a">
                                        {item?.subContentList?.length > 0 ? item?.subContentList.map((el: any, elInd: number) => {
                                            return (
                                                <div key={elInd + 1}>
                                                    <li style={{ ...styles.termsContent, marginTop: "0px" }}>
                                                        <Typography style={{ ...styles.termsContent, marginTop: "0px" }}>
                                                            <Trans
                                                                i18nKey={el?.body}
                                                                components={i18nCustomComponents} />
                                                        </Typography>
                                                    </li>
                                                    <ol type="i">

                                                        {el?.bodyList?.length > 0 ? el?.bodyList.map((val: any, valInd: number) => {
                                                            return (
                                                                <li key={valInd + 1} style={{ ...styles.termsContent, marginTop: "0px" }}>
                                                                    <Trans
                                                                        i18nKey={val?.listContent}
                                                                        components={i18nCustomComponents} />
                                                                </li>
                                                            )
                                                        }) : null}
                                                    </ol>
                                                </div>
                                            )
                                        }) : null}
                                    </ol>
                                </Box>
                            )
                        })
                    }
                </Box>
            </Box>
            <Box sx={ isPrivacy ? {...styles.checkBoxWrapper, mt:"4.875rem", pt:4} : styles.checkBoxWrapper }>
                <Box sx={{ display: "flex", alignItems: "center", flexDirection: "row" }}>
                    <Checkbox
                        disabled={!isPrivacy && !isScrolledToBottom}
                        value={checked}
                        onClick={() => setChecked(!checked)} />
                    <Typography sx={{ ...theme.typography.body1Regular, textAlign: "left" }}>{checkboxContent}<span style={{ color: "#EA3232" }}>*</span></Typography>
                </Box>
                <Button
                    onClick={() => onButtonClick && onButtonClick(checked)}
                    variant='contained'
                    sx={{ ...theme.button.contained.primary, width: "85px" }}>
                    <Typography sx={styles.buttonLabel}>{buttonLabel}</Typography>
                </Button>
            </Box>
        </Box>
    );
}